// Admin · Ops tab — System scheduler triggers + PRO broadcast composer + history.
// Endpoints:
//   POST /api/admin/curators/cycles/run
//   POST /api/admin/curators/wyckoff/run
//   GET  /api/admin/broadcast/pro/preview
//   POST /api/admin/broadcast/pro
//   GET  /api/admin/broadcast/pro/history
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Send, Megaphone, RefreshCw, Image as ImageIcon, X, Upload, ExternalLink } from "lucide-react";

import { API } from "../../../lib/api";
import { useLanguage } from "../../../i18n/LanguageContext";

const COPY = {
  schedTitle: { es: "Schedulers · Ejecución manual", en: "Schedulers · Manual run", sk: "Plánovače · Manuálny beh", de: "Scheduler · Manuelle Ausführung", "zh-CN": "调度器 · 手动执行" },
  schedDesc: {
    es: "Fuerza una ejecución inmediata del auto-poster. Útil tras un redeploy fresco.",
    en: "Force immediate run of the auto-poster. Useful after a fresh redeploy.",
    sk: "Vynúť okamžitý beh auto-postera. Užitočné po čerstvom nasadení.", de: "Erzwingt eine sofortige Ausführung des Auto-Posters. Nützlich nach einem frischen Redeploy.", "zh-CN": "强制立即执行自动发布器。适用于全新重新部署后。",
  },
  runCycles: { es: "🔄 Ciclos: Ejecutar ahora", en: "🔄 Cycles: Run now", sk: "🔄 Cycles: Spusti teraz", de: "🔄 Zyklen: Jetzt ausführen", "zh-CN": "🔄 周期：立即执行" },
  runWyckoff: { es: "🔄 Wyckoff: Ejecutar ahora", en: "🔄 Wyckoff: Run now", sk: "🔄 Wyckoff: Spusti teraz", de: "🔄 Wyckoff: Jetzt ausführen", "zh-CN": "🔄 Wyckoff：立即执行" },
  bcTitle: { es: "📢 Avisos a usuarios PRO", en: "📢 PRO user broadcasts", sk: "📢 Oznámenia pre PRO používateľov", de: "📢 Mitteilungen an PRO-Nutzer", "zh-CN": "📢 PRO 用户通知" },
  bcDesc: {
    es: "Envía un aviso simultáneamente por campana 🔔 y email a todos los PRO. Rate limit: 1/hora.",
    en: "Send a broadcast to all PRO users via 🔔 and email. Rate limit: 1/hour.",
    sk: "Odošli oznámenie všetkým PRO používateľom cez 🔔 aj email. Limit: 1/hodina.", de: "Sende gleichzeitig eine Benachrichtigung per Glocke 🔔 und E-Mail an alle PRO. Rate Limit: 1/Stunde.", "zh-CN": "同时通过铃铛 🔔 和电子邮件向所有 PRO 用户发送通知。速率限制：1次/小时。",
  },
  fTitle: { es: "Título (máx 80)", en: "Title (max 80)", sk: "Nadpis (max 80)", de: "Titel (max. 80)", "zh-CN": "标题（最多80个字符）" },
  fMsg: { es: "Mensaje (máx 500)", en: "Message (max 500)", sk: "Správa (max 500)", de: "Nachricht (max. 500)", "zh-CN": "消息（最多 500 字符）" },
  fImg: { es: "Imagen (opcional)", en: "Image (optional)", sk: "Obrázok (voliteľné)", de: "Bild (optional)", "zh-CN": "图片（可选）" },
  imgHint: { es: "Pega la URL de una imagen (Cloudinary, Imgur, etc).", en: "Paste an image URL (Cloudinary, Imgur, etc).", sk: "Vlož URL obrázka (Cloudinary, Imgur, atď.).", de: "Füge die URL eines Bildes ein (Cloudinary, Imgur, etc.).", "zh-CN": "粘贴图片 URL（Cloudinary、Imgur 等）。" },
  dropZone: { es: "Arrastra una imagen aquí o", en: "Drag an image here or", sk: "Presuň obrázok sem alebo", de: "Ziehe ein Bild hierher oder", "zh-CN": "将图片拖到此处或" },
  dropZonePick: { es: "elige un archivo", en: "pick a file", sk: "vyber súbor", de: "Wähle eine Datei", "zh-CN": "选择一个文件" },
  dropZoneHint: { es: "JPG · PNG · WebP · max 5 MB", en: "JPG · PNG · WebP · max 5 MB", sk: "JPG · PNG · WebP · max 5 MB", de: "JPG · PNG · WebP · max. 5 MB", "zh-CN": "JPG · PNG · WebP · 最大 5 MB" },
  uploading: { es: "Subiendo…", en: "Uploading…", sk: "Nahrávam…", de: "Wird hochgeladen…", "zh-CN": "上传中…" },
  uploadErr: { es: "Error al subir. Reintenta o pega URL manualmente:", en: "Upload failed. Retry or paste URL manually:", sk: "Nahrávanie zlyhalo. Skús znova alebo vlož URL ručne:", de: "Fehler beim Hochladen. Versuche es erneut oder füge die URL manuell ein:", "zh-CN": "上传失败。请重试或手动粘贴 URL：" },
  removeImg: { es: "Quitar imagen", en: "Remove image", sk: "Odstrániť obrázok", de: "Bild entfernen", "zh-CN": "移除图片" },
  urlManual: { es: "URL manual (fallback)", en: "Manual URL (fallback)", sk: "Ručná URL (záloha)", de: "Manuelle URL (Fallback)", "zh-CN": "手动 URL（备用）" },
  preview: { es: "Vista previa", en: "Preview", sk: "Náhľad", de: "Vorschau", "zh-CN": "预览" },
  sendBtn: { es: "Enviar a todos los PRO", en: "Send to all PRO", sk: "Odoslať všetkým PRO", de: "An alle PRO senden", "zh-CN": "发送给所有 PRO 用户" },
  confirm: (n) => ({
    es: `¿Estás seguro? Esto enviará el aviso a ${n} usuarios PRO.`,
    en: `Are you sure? This will send the announcement to ${n} PRO users.`,
    sk: `Si si istý? Toto pošle oznámenie ${n} PRO používateľom.`, de: `Bist du sicher? Dies sendet die Benachrichtigung an ${n} PRO-Benutzer.`, "zh-CN": `确定吗？这将向 ${n} 位 PRO 用户发送通知。`,
  }),
  histTitle: { es: "Historial (últimos 10)", en: "History (last 10)", sk: "História (posledných 10)", de: "Verlauf (letzte 10)", "zh-CN": "历史记录（最近 10 条）" },
  histEmpty: { es: "Aún no hay broadcasts.", en: "No broadcasts yet.", sk: "Zatiaľ žiadne broadcasty.", de: "Noch keine Broadcasts vorhanden.", "zh-CN": "暂无直播。" },
  proCount: { es: "usuarios PRO activos", en: "active PRO users", sk: "aktívnych PRO používateľov", de: "aktive PRO-Nutzer", "zh-CN": "活跃 PRO 用户" },
  rateLimited: {
    es: "Rate limit: espera hasta",
    en: "Rate limited until",
    sk: "Limit do", de: "Rate-Limit: warte bis", "zh-CN": "速率限制：请等待至",
  },
  cacheTitle: {
    es: "🔄 Refrescar preview en X / Twitter",
    en: "🔄 Refresh X / Twitter preview",
    sk: "🔄 Obnov preview na X / Twitter", de: "🔄 Vorschau auf X / Twitter aktualisieren", "zh-CN": "🔄 刷新 X / Twitter 预览",
  },
  cacheDesc: {
    es: "X cachea previews de OG hasta 7 días. Pega el ID del post y abre el Card Validator de X para forzar un re-crawl inmediato.",
    en: "X caches OG previews for up to 7 days. Paste the post ID and open the X Card Validator to force an immediate re-crawl.",
    sk: "X cachuje OG preview až 7 dní. Vlož ID postu a otvor X Card Validator na okamžitý re-crawl.", de: "X speichert OG-Vorschauen bis zu 7 Tage im Cache. Füge die Post-ID ein und öffne den Card Validator von X, um ein sofortiges erneutes Crawling zu erzwingen.", "zh-CN": "X 会缓存 OG 预览最多 7 天。粘贴帖子 ID 并打开 X 的 Card Validator 以强制立即重新抓取。",
  },
  cachePlaceholder: {
    es: "post ID (ej. 6a465308603bbb00d1bf3f13)",
    en: "post ID (e.g. 6a465308603bbb00d1bf3f13)",
    sk: "ID postu (napr. 6a465308603bbb00d1bf3f13)", de: "Post-ID (z.B. 6a465308603bbb00d1bf3f13)", "zh-CN": "帖子 ID（例如 6a465308603bbb00d1bf3f13）",
  },
  cacheOpen: { es: "Abrir en X Card Validator", en: "Open in X Card Validator", sk: "Otvor v X Card Validator", de: "In X Card Validator öffnen", "zh-CN": "在 X Card Validator 中打开" },
  cacheEmpty: { es: "Ingresa un ID válido primero.", en: "Enter a valid ID first.", sk: "Najprv zadaj platné ID.", de: "Geben Sie zuerst eine gültige ID ein.", "zh-CN": "请先输入有效的 ID。" },
};

const t = (key, lang, arg) => {
  const val = COPY[key];
  if (!val) return key;
  if (typeof val === "function") return val(arg)[lang] || val(arg).es;
  return val[lang] || val.en || val.es;
};

// ─── Schedulers block ─────────────────────────────────────────────────
function SchedulerCard({ token, endpoint, label, testid, allowForce = false }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [force, setForce] = useState(false);

  const run = async () => {
    if (!token) return;
    setBusy(true);
    setResult(null);
    try {
      const url = allowForce && force ? `${API}${endpoint}?force=true` : `${API}${endpoint}`;
      const r = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.clone().json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setResult(d);
      toast.success(`✅ ${label.split(":")[0]} · posts=${d.posts_total} dedupe=${d.dedupe_entries}${d.forced ? " · FORCED" : ""}`);
    } catch (e) {
      toast.error(`❌ ${String(e.message || e).slice(0, 140)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid={testid}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#f97316]/40 bg-[#f97316]/[0.08] px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-[1.5px] text-[#f97316] transition-all hover:bg-[#f97316]/[0.18] disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        {label}
        {allowForce && force ? " · FORCE" : ""}
      </button>
      {allowForce && (
        <label className="mt-2 flex items-center justify-center gap-2 font-mono text-[10px] text-white/60 cursor-pointer">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            data-testid={`${testid}-force`}
            className="h-3 w-3 accent-[#f97316]"
          />
          <span title="Ignora dedupe/daily cap. Sólo validación técnica.">
            force · bypass dedupe (test)
          </span>
        </label>
      )}
      {result && (
        <div className="mt-3 rounded-lg border border-white/10 bg-black/40 p-3 font-mono text-[10px] text-white/75" data-testid={`${testid}-result`}>
          <div className="flex flex-wrap gap-3 text-white/85">
            <span>posts_total: <b className="text-[#00d084]">{result.posts_total}</b></span>
            <span>dedupe: <b>{result.dedupe_entries}</b></span>
          </div>
          {result.latest_posts?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {result.latest_posts.slice(0, 3).map((p) => (
                <li key={p.id} className="truncate">
                  · {p.title}
                  {p.image_url && (
                    <a href={p.image_url} target="_blank" rel="noreferrer" className="ml-2 text-[#f97316] underline">img</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Image dropzone (drag&drop → Cloudinary) ─────────────────────────
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

function ImageDropzone({ token, value, onChange, lang }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [manualMode, setManualMode] = useState(false);

  const doUpload = useCallback(async (file) => {
    setErr(null);
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      setErr(`invalid_mime: ${file.type || "unknown"}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr("max_5mb");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/admin/broadcast/pro/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await r.clone().json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      if (!d.url) throw new Error("no_url_returned");
      onChange(d.url);
    } catch (e) {
      setErr(String(e.message || e).slice(0, 140));
    } finally {
      setUploading(false);
    }
  }, [token, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";  // reset para permitir re-subir el mismo file
  };

  if (value) {
    return (
      <div className="relative">
        <img
          src={value}
          alt=""
          data-testid="admin-broadcast-image-thumb"
          className="w-full max-h-40 rounded-md object-cover border border-white/10"
          onError={() => setErr("image_load_failed")}
        />
        <button
          type="button"
          onClick={() => { onChange(""); setErr(null); setManualMode(false); }}
          data-testid="admin-broadcast-image-remove"
          title={t("removeImg", lang)}
          className="absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white/80 hover:bg-red-500/80 hover:border-red-400"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        data-testid="admin-broadcast-dropzone"
        className={`cursor-pointer rounded-md border-2 border-dashed px-4 py-6 text-center transition-all ${
          dragOver
            ? "border-[#f97316] bg-[#f97316]/10"
            : uploading
              ? "border-white/30 bg-white/[0.02]"
              : "border-white/15 bg-black/30 hover:border-[#f97316]/50"
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 font-mono text-[11px] text-white/70">
            <Loader2 className="h-5 w-5 animate-spin text-[#f97316]" />
            {t("uploading", lang)}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <Upload className="h-5 w-5 text-white/50" />
            <p className="font-mono text-[11px] text-white/70">
              {t("dropZone", lang)}{" "}
              <span className="text-[#f97316] underline">{t("dropZonePick", lang)}</span>
            </p>
            <p className="font-mono text-[9px] text-white/40">{t("dropZoneHint", lang)}</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onFilePicked}
          className="hidden"
          data-testid="admin-broadcast-file-input"
        />
      </div>
      {err && (
        <div className="rounded-md border border-red-400/30 bg-red-400/[0.08] px-3 py-2 font-mono text-[10px] text-red-300"
             data-testid="admin-broadcast-upload-error">
          ⚠ {err}. {t("uploadErr", lang)}{" "}
          <button
            type="button"
            className="ml-1 underline"
            onClick={() => setManualMode(true)}
          >
            {t("urlManual", lang)}
          </button>
        </div>
      )}
      {(manualMode || err) && (
        <input
          type="url"
          onChange={(e) => onChange(e.target.value)}
          data-testid="admin-broadcast-image-input"
          className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-[11px] text-white outline-none focus:border-[#f97316]"
          placeholder="https://res.cloudinary.com/..."
        />
      )}
    </div>
  );
}

// ─── Broadcast composer ───────────────────────────────────────────────
function BroadcastComposer({ token, onSent }) {
  const { lang } = useLanguage();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const fetchPreview = useCallback(async () => {
    if (!token) return;
    setPreviewLoading(true);
    try {
      const r = await fetch(`${API}/admin/broadcast/pro/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.clone().json();
      setPreview(d);
    } catch {
      /* silent */
    } finally {
      setPreviewLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const rateLimited = useMemo(() => {
    if (!preview?.rate_limited_until) return null;
    const dt = new Date(preview.rate_limited_until);
    return dt.getTime() > Date.now() ? dt : null;
  }, [preview]);

  const inCooldown = cooldownUntil > Date.now();
  const canSend = !sending && !inCooldown && !rateLimited && title.trim() && message.trim() && (preview?.pro_count || 0) > 0;

  const send = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/admin/broadcast/pro`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title.trim(),
          message: message.trim(),
          image_url: imageUrl.trim() || null,
        }),
      });
      const d = await r.clone().json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`✅ notif=${d.users_notified} · email=${d.emails_sent}${d.errors_count ? ` · errors=${d.errors_count}` : ""}`);
      setTitle(""); setMessage(""); setImageUrl("");
      setCooldownUntil(Date.now() + 60_000);
      fetchPreview();
      onSent?.();
    } catch (e) {
      toast.error(`❌ ${String(e.message || e).slice(0, 180)}`);
    } finally {
      setSending(false);
    }
  };

  const confirmSend = () => {
    const n = preview?.pro_count ?? 0;
    const val = COPY.confirm(n);
    const msg = val[lang] || val.en || val.es;
    if (window.confirm(msg)) send();
  };

  return (
    <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <header className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-[#f97316]" />
        <h3 className="font-mono text-[12px] font-bold uppercase tracking-[2px] text-[#f97316]">{t("bcTitle", lang)}</h3>
      </header>
      <p className="font-mono text-[10px] text-white/50">{t("bcDesc", lang)}</p>

      <div className="flex items-center gap-3 font-mono text-[10px] flex-wrap">
        <span className="rounded-md border border-[#00d084]/30 bg-[#00d084]/[0.08] px-2 py-1 text-[#00d084]">
          <b>{previewLoading ? "…" : preview?.pro_count ?? 0}</b> {t("proCount", lang)}
        </span>
        {rateLimited && (
          <span className="rounded-md border border-red-400/30 bg-red-400/[0.08] px-2 py-1 text-red-300">
            ⏳ {t("rateLimited", lang)} {rateLimited.toISOString().slice(11, 16)} UTC
          </span>
        )}
        {preview && preview.email_provider_ready === false && (
          <span
            data-testid="admin-broadcast-email-warn"
            className="rounded-md border border-yellow-400/30 bg-yellow-400/[0.08] px-2 py-1 text-yellow-300"
            title="RESEND_API_KEY missing — solo notif in-app, no email"
          >
            ⚠ email off
          </span>
        )}
        <button
          type="button"
          onClick={fetchPreview}
          className="ml-auto inline-flex items-center gap-1 text-white/50 hover:text-white"
          data-testid="admin-broadcast-refresh"
        >
          <RefreshCw className="h-3 w-3" /> refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Form column */}
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-[1.5px] text-white/55">{t("fTitle", lang)}</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              maxLength={80}
              data-testid="admin-broadcast-title-input"
              className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-[12px] text-white outline-none focus:border-[#f97316]"
              placeholder="Nuevas features PRO..."
            />
            <span className="mt-1 block text-right font-mono text-[9px] text-white/35">{title.length}/80</span>
          </label>

          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-[1.5px] text-white/55">{t("fMsg", lang)}</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              rows={5}
              maxLength={500}
              data-testid="admin-broadcast-message-input"
              className="w-full resize-y rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-[12px] text-white outline-none focus:border-[#f97316]"
              placeholder="Cuerpo del aviso..."
            />
            <span className="mt-1 block text-right font-mono text-[9px] text-white/35">{message.length}/500</span>
          </label>

          <div className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-[1.5px] text-white/55">
              <ImageIcon className="mr-1 inline h-3 w-3" /> {t("fImg", lang)}
            </span>
            <ImageDropzone token={token} value={imageUrl} onChange={setImageUrl} lang={lang} />
          </div>

          <button
            type="button"
            onClick={confirmSend}
            disabled={!canSend}
            data-testid="admin-broadcast-send-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#00d084]/50 bg-[#00d084]/[0.10] px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[2px] text-[#00d084] transition-all hover:bg-[#00d084]/[0.20] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {inCooldown ? `${Math.ceil((cooldownUntil - Date.now()) / 1000)}s` : t("sendBtn", lang)}
          </button>
        </div>

        {/* Preview column */}
        <div className="space-y-2">
          <span className="block font-mono text-[10px] uppercase tracking-[1.5px] text-white/55">{t("preview", lang)}</span>
          <div
            data-testid="admin-broadcast-preview"
            className="rounded-xl border border-[#f97316]/30 bg-gradient-to-br from-[#1a1408] to-[#0a0a0c] p-4"
            style={{ minHeight: 180 }}
          >
            <div className="flex items-start gap-2">
              <div
                style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "rgba(249,115,22,0.18)",
                  display: "inline-flex", alignItems: "center",
                  justifyContent: "center", fontSize: 16,
                }}
              >📢</div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[12px] font-bold text-[#f97316]">{title || <span className="opacity-50">Título…</span>}</p>
                <p className="mt-1 font-mono text-[11px] text-white/80 whitespace-pre-wrap">{message || <span className="opacity-40">Mensaje…</span>}</p>
                {imageUrl && (
                  <img src={imageUrl} alt="preview" className="mt-2 max-h-40 w-full rounded-md object-cover border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── History table ─────────────────────────────────────────────────────
function BroadcastHistory({ token, refreshKey }) {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/broadcast/pro/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.clone().json();
      setItems(d.items || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-mono text-[11px] font-bold uppercase tracking-[2px] text-white/70">{t("histTitle", lang)}</h3>
        <button type="button" onClick={load} className="text-white/50 hover:text-white" data-testid="admin-broadcast-history-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="py-4 text-center font-mono text-[10px] text-white/40">{t("histEmpty", lang)}</p>
      ) : (
        <ul className="divide-y divide-white/5" data-testid="admin-broadcast-history-list">
          {items.map((b) => (
            <li key={b.id} className="flex items-start gap-3 py-2.5">
              <span className="mt-0.5 rounded-full bg-[#f97316]/15 px-2 py-0.5 font-mono text-[9px] font-bold text-[#f97316]">
                {b.users_notified}👤
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-[11px] text-white/85"><b>{b.title}</b></p>
                <p className="mt-0.5 font-mono text-[10px] text-white/50">
                  {b.sent_at?.slice(0, 16).replace("T", " ")} · {b.admin_email} · email={b.emails_sent}
                  {b.errors_count ? ` · errors=${b.errors_count}` : ""}
                </p>
              </div>
              {b.image_url && (
                <a href={b.image_url} target="_blank" rel="noreferrer" title="img">
                  <ImageIcon className="h-3.5 w-3.5 text-[#f97316]" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── OG cache refresh (X Card Validator shortcut) ─────────────────────
// X/Twitter no expone una API pública para invalidar el cache OG. La única
// forma soportada es pasar el URL por el Card Validator UI:
//   https://cards-dev.twitter.com/validator?url=<encoded>
// Aunque el validator es viejo, sigue funcionando y fuerza a Twitterbot a
// re-crawlear el URL en el momento. Este componente sólo abre esa página
// pre-cargada con el URL correcto del post.
const _isValidPostId = (s) => /^[a-f0-9]{24}$/i.test((s || "").trim());

function OGCacheRefresh() {
  const { lang } = useLanguage();
  const [id, setId] = useState("");

  const openValidator = () => {
    const trimmed = id.trim();
    if (!_isValidPostId(trimmed)) {
      toast.error(t("cacheEmpty", lang));
      return;
    }
    // Cache-bust `?v=` para forzar scrape fresh en X aún si el URL fue cacheado antes.
    const postUrl = `${window.location.origin}/api/og/post/${trimmed}?v=${Date.now()}`;
    const validator = `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(postUrl)}`;
    window.open(validator, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="admin-og-cache-tools">
      <header className="mb-3">
        <h3 className="font-mono text-[12px] font-bold uppercase tracking-[2px] text-[#f97316]">{t("cacheTitle", lang)}</h3>
        <p className="mt-1 font-mono text-[10px] text-white/50">{t("cacheDesc", lang)}</p>
      </header>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <input
          type="text"
          value={id}
          onChange={(e) => setId(e.target.value)}
          data-testid="admin-og-cache-id-input"
          placeholder={t("cachePlaceholder", lang)}
          className="flex-1 rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-[11px] text-white outline-none focus:border-[#f97316]"
        />
        <button
          type="button"
          onClick={openValidator}
          disabled={!_isValidPostId(id)}
          data-testid="admin-og-cache-open-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-[#f97316]/40 bg-[#f97316]/[0.08] px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[1.5px] text-[#f97316] transition-all hover:bg-[#f97316]/[0.18] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t("cacheOpen", lang)}
        </button>
      </div>
    </div>
  );
}

// ─── Root Ops tab ────────────────────────────────────────────────────
export default function OpsTab({ token }) {
  const { lang } = useLanguage();
  const [refreshHistory, setRefreshHistory] = useState(0);
  return (
    <div className="space-y-6" data-testid="admin-ops-tab">
      {/* Schedulers */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <header className="mb-3">
          <h3 className="font-mono text-[12px] font-bold uppercase tracking-[2px] text-[#f97316]">{t("schedTitle", lang)}</h3>
          <p className="mt-1 font-mono text-[10px] text-white/50">{t("schedDesc", lang)}</p>
        </header>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SchedulerCard token={token} endpoint="/admin/curators/cycles/run" label={t("runCycles", lang)} testid="admin-run-cycles-btn" allowForce />
          <SchedulerCard token={token} endpoint="/admin/curators/wyckoff/run" label={t("runWyckoff", lang)} testid="admin-run-wyckoff-btn" />
        </div>
      </div>

      {/* Broadcast composer */}
      <BroadcastComposer token={token} onSent={() => setRefreshHistory((k) => k + 1)} />

      {/* History */}
      <BroadcastHistory token={token} refreshKey={refreshHistory} />

      {/* OG cache tools */}
      <OGCacheRefresh />
    </div>
  );
}
